import { PageHeader, Loader, EmptyState } from "@/components/common/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { UserCog, Plus, CalendarDays, Trash2, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const ATT = {
  present: "bg-emerald-100 text-emerald-700",
  late: "bg-amber-100 text-amber-700",
  absent: "bg-red-100 text-red-700",
  leave: "bg-stone-200 text-stone-600",
};

const EMPTY_FORM = {
  name: "",
  phone: "",
  role: "",
  email: "",
  password: "",
};

// Suggestions only. These are NOT restaurant roles and are never persisted
// until the owner explicitly clicks "Add Role" and creates one.
const SUGGESTED_ROLES = [
  "Manager",
  "Supervisor",
  "Cashier",
  "Captain",
  "Kitchen",
  "Inventory Manager",
];

const roleLabel = (role = "") =>
  String(role)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const roleValue = (label = "") =>
  String(label)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export default function Staff() {
  const { branchId, branches } = useApp();

  const [staff, setStaff] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [view, setView] = useState("team");

  // Prevent duplicate staff creation/update from repeated clicks.
  const [savingStaff, setSavingStaff] = useState(false);

  // Common confirmation dialog state.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmStaff, setConfirmStaff] = useState(null);
  const [deletingStaff, setDeletingStaff] = useState(false);

  const [attendance, setAttendance] = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [attendanceStatus, setAttendanceStatus] = useState("all");
  const [attendanceSearch, setAttendanceSearch] = useState("");
  const [attendanceStaff, setAttendanceStaff] = useState("all");

  const [pwOpen, setPwOpen] = useState(false);
  const [pwStaff, setPwStaff] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const now = new Date();

  const [historyYear, setHistoryYear] = useState(now.getFullYear().toString());

  const [historyMonth, setHistoryMonth] = useState(
    String(now.getMonth() + 1).padStart(2, "0"),
  );

  const [monthlyAttendance, setMonthlyAttendance] = useState([]);

  const [noteOpen, setNoteOpen] = useState(false);
  const [noteStaff, setNoteStaff] = useState(null);
  const [noteStatus, setNoteStatus] = useState("");
  const [attendanceNote, setAttendanceNote] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);

  // Restaurant-created custom roles.
  const [customRoles, setCustomRoles] = useState([]);
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [savingRole, setSavingRole] = useState(false);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setEditingStaff(null);
  };

  const openAddStaff = () => {
    resetForm();
    setOpen(true);
  };

  const startEdit = (s) => {
    setEditingStaff(s);

    setForm({
      name: s.name || "",
      phone: s.phone || "",
      role: s.role || "",
      email: s.email || "",
      password: "",
    });

    setOpen(true);
  };

  const openDeleteConfirm = (s) => {
    setConfirmStaff(s);
    setConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deletingStaff) return;
    setConfirmOpen(false);
    setConfirmStaff(null);
  };

  const deleteStaff = async () => {
    if (!confirmStaff || deletingStaff) return;

    const s = confirmStaff;
    setDeletingStaff(true);

    try {
      await api.deleteStaff(s.id);
      toast.success(`${s.name} removed`);

      const r = await api.staff(branchId);
      setStaff(r.data);

      setConfirmOpen(false);
      setConfirmStaff(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove staff");
    } finally {
      setDeletingStaff(false);
    }
  };

  const openResetPassword = (s) => {
    setPwStaff(s);
    setNewPassword("");
    setPwOpen(true);
  };

  const savePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await api.resetStaffPassword(pwStaff.id, newPassword);

      toast.success("Password updated");
      setPwOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update password");
    }
  };

  const saveStaff = async () => {
    if (savingStaff) return;

    const name = form.name.trim();
    const phone = form.phone.trim();
    const role = form.role.trim();

    if (!name) {
      toast.error("Full name is required");
      return;
    }

    if (name.length < 2) {
      toast.error("Full name must be at least 2 characters");
      return;
    }

    if (name.length > 100) {
      toast.error("Full name must be 100 characters or less");
      return;
    }

    if (!phone) {
      toast.error("Phone number is required");
      return;
    }

    const phoneDigits = phone.replace(/\D/g, "");

    if (!/^\d{10}$/.test(phoneDigits)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!role) {
      toast.error("Please select or create a role");
      return;
    }

    if (!editingStaff) {
      const email = form.email.trim();

      if (!email) {
        toast.error("Email is required to create a staff login");
        return;
      }

      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!emailValid) {
        toast.error("Please enter a valid email address");
        return;
      }

      if (!form.password || form.password.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }

      if (form.password.length > 128) {
        toast.error("Password must be 128 characters or less");
        return;
      }
    }

    setSavingStaff(true);

    try {
      const activeBranch = branchId === "all" ? branches[0]?.id : branchId;

      if (!activeBranch) {
        toast.error("No active branch selected");
        return;
      }

      if (editingStaff) {
        await api.updateStaff(editingStaff.id, {
          name,
          phone,
          role,
          email: form.email.trim(),
        });

        toast.success("Staff updated");
      } else {
        await api.createStaff({
          name,
          phone,
          role,
          email: form.email.trim(),
          password: form.password,
          branch_id: activeBranch,
        });

        toast.success("Staff added — they can now log in with their email");
      }

      setOpen(false);
      resetForm();

      api.staff(branchId).then((r) => setStaff(r.data));
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          (editingStaff ? "Failed to update staff" : "Failed to add staff"),
      );
    } finally {
      setSavingStaff(false);
    }
  };

  const loadCustomRoles = async () => {
    try {
      const response = await api.settings();
      const settings = response.data?.settings || {};
      const roles = Array.isArray(settings.custom_roles)
        ? settings.custom_roles
        : [];

      setCustomRoles(
        roles
          .filter((role) => role && role.value && role.label)
          .map((role) => ({
            value: String(role.value),
            label: String(role.label),
          })),
      );
    } catch (e) {
      console.error("Failed to load custom roles", e);
      setCustomRoles([]);
    }
  };

  const openAddRole = () => {
    setNewRoleName("");
    setAddRoleOpen(true);
  };

  const closeAddRole = () => {
    if (savingRole) return;
    setAddRoleOpen(false);
    setNewRoleName("");
  };

  // Add a role directly when the owner clicks a suggested role.
  // Suggestions are only templates until the owner clicks them.
  const addSuggestedRole = async (label) => {
    if (savingRole) return;

    const cleanLabel = String(label || "").trim();
    const value = roleValue(cleanLabel);

    if (!cleanLabel || !value) {
      toast.error("Invalid suggested role");
      return;
    }

    const existingRole = customRoles.find((role) => role.value === value);

    if (existingRole) {
      setForm((current) => ({
        ...current,
        role: existingRole.value,
      }));
      return;
    }

    setSavingRole(true);

    try {
      const nextRoles = [
        ...customRoles,
        {
          value,
          label: cleanLabel,
        },
      ];

      // Roles are stored in the authenticated restaurant's settings.
      await api.updateSettings({
        custom_roles: nextRoles,
      });

      setCustomRoles(nextRoles);
      setForm((current) => ({
        ...current,
        role: value,
      }));

      toast.success(`${cleanLabel} role added`);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to add role");
    } finally {
      setSavingRole(false);
    }
  };

  const saveCustomRole = async () => {
    if (savingRole) return;

    const label = newRoleName.trim().replace(/\s+/g, " ");

    if (!label) {
      toast.error("Role name is required");
      return;
    }

    if (label.length < 2) {
      toast.error("Role name must be at least 2 characters");
      return;
    }

    if (label.length > 50) {
      toast.error("Role name must be 50 characters or less");
      return;
    }

    const value = roleValue(label);

    if (!value) {
      toast.error("Please enter a valid role name");
      return;
    }

    const allRoleValues = [
      ...SUGGESTED_ROLES.map((role) => roleValue(role)),
      ...customRoles.map((role) => role.value),
    ];

    if (allRoleValues.includes(value)) {
      const existingCustomRole = customRoles.find(
        (role) => role.value === value,
      );

      if (existingCustomRole) {
        setForm((current) => ({
          ...current,
          role: existingCustomRole.value,
        }));
        closeAddRole();
        toast.success(`${existingCustomRole.label} selected`);
      } else {
        toast.error(
          "This is a suggested role. Click the suggestion to add it.",
        );
      }
      return;
    }

    setSavingRole(true);

    try {
      const nextRoles = [
        ...customRoles,
        {
          value,
          label,
        },
      ];

      // Roles are stored in the authenticated restaurant's settings.
      await api.updateSettings({
        custom_roles: nextRoles,
      });

      setCustomRoles(nextRoles);
      setForm((current) => ({
        ...current,
        role: value,
      }));

      setAddRoleOpen(false);
      setNewRoleName("");

      toast.success(`${label} role added`);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to add role");
    } finally {
      setSavingRole(false);
    }
  };

  const loadAttendance = () => {
    api
      .attendance({
        branch_id: branchId,
        date: attendanceDate,
      })
      .then((r) => setAttendance(r.data))
      .catch(() => setAttendance([]));
  };

  const loadMonthlyAttendance = () => {
    api
      .attendance({
        branch_id: branchId,
        month: `${historyYear}-${historyMonth}`,
      })
      .then((r) => setMonthlyAttendance(r.data))
      .catch(() => setMonthlyAttendance([]));
  };

  const openAttendanceNote = (staffMember, status) => {
    setNoteStaff(staffMember);
    setNoteStatus(status);
    setAttendanceNote("");
    setNoteOpen(true);
  };

  const markAttendance = async (staffMember, status, note = "") => {
    try {
      const activeBranch = branchId === "all" ? branches[0]?.id : branchId;

      if (!activeBranch) {
        toast.error("No active branch selected");
        return;
      }

      await api.markAttendance({
        staff_id: staffMember.id,
        branch_id: activeBranch,
        date: attendanceDate,
        status,
        note,
      });

      toast.success(`${staffMember.name}: ${status}`);

      loadAttendance();
      loadMonthlyAttendance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark attendance");
    }
  };

  useEffect(() => {
    setStaff(null);

    api
      .staff(branchId)
      .then((r) => setStaff(r.data))
      .catch(() => setStaff([]));

    loadCustomRoles();
  }, [branchId]);

  useEffect(() => {
    if (view === "attendance") {
      loadAttendance();
      loadMonthlyAttendance();
    }
  }, [view, branchId, attendanceDate, historyYear, historyMonth]);

  if (!staff) {
    return (
      <div className="p-6">
        <Loader label="Loading staff" />
      </div>
    );
  }

  const dayLabel = (count) => `${count} ${count === 1 ? "day" : "days"}`;

  const monthlySummary = staff.map((s) => {
    const records = monthlyAttendance.filter((a) => a.staff_id === s.id);

    const daysInMonth = new Date(
      Number(historyYear),
      Number(historyMonth),
      0,
    ).getDate();

    return {
      ...s,
      present: records.filter((a) => a.status === "present").length,
      late: records.filter((a) => a.status === "late").length,
      leave: records.filter((a) => a.status === "leave").length,
      absent: records.filter((a) => a.status === "absent").length,
      notMarked: Math.max(daysInMonth - records.length, 0),
    };
  });

  const filteredStaff = staff.filter((s) => {
    const record = attendance.find(
      (a) => a.staff_id === s.id && a.date === attendanceDate,
    );

    const status = record?.status || "not marked";

    const matchesStatus =
      attendanceStatus === "all" || status === attendanceStatus;

    const matchesSearch =
      !attendanceSearch ||
      s.name.toLowerCase().includes(attendanceSearch.toLowerCase()) ||
      s.phone?.includes(attendanceSearch);

    const matchesStaff = attendanceStaff === "all" || s.id === attendanceStaff;

    return matchesStatus && matchesSearch && matchesStaff;
  });

  return (
    <>
      <div className="p-4 sm:p-6 animate-fade-up">
        {/* Team / Attendance switch */}
        <div className="flex items-center gap-2 mb-5">
          <Button
            variant={view === "team" ? "default" : "outline"}
            onClick={() => setView("team")}
            className="rounded-xl"
          >
            <UserCog size={16} className="mr-1.5" />
            Team
          </Button>

          <Button
            variant={view === "attendance" ? "default" : "outline"}
            onClick={() => setView("attendance")}
            className="rounded-xl"
          >
            <CalendarDays size={16} className="mr-1.5" />
            Attendance
          </Button>
        </div>

        <PageHeader
          title="Staff"
          subtitle={`${staff.length} team members`}
          actions={
            <Button
              onClick={openAddStaff}
              className="rounded-xl font-semibold"
              data-testid="staff-add"
            >
              <Plus size={16} className="mr-1" />
              Add Staff
            </Button>
          }
        />

        {view === "attendance" ? (
          <>
            {staff.length === 0 ? (
              <EmptyState
                icon={UserCog}
                title="No staff members yet"
                subtitle="Add staff members first to start managing attendance."
              />
            ) : (
              <>
                {/* Daily Attendance */}
                <div className="rounded-2xl bg-card border border-border overflow-hidden">
                  <div className="p-5 border-b border-border">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h2 className="font-semibold">Attendance</h2>

                        <p className="text-sm text-muted-foreground">
                          Manage daily staff attendance
                        </p>
                      </div>

                      <div className="ml-auto flex items-center gap-2">
                        <Input
                          type="date"
                          className="h-10 rounded-xl w-auto"
                          value={attendanceDate}
                          onChange={(e) => setAttendanceDate(e.target.value)}
                        />

                        <select
                          value={attendanceStatus}
                          onChange={(e) => setAttendanceStatus(e.target.value)}
                          className="h-10 rounded-xl border border-input bg-background px-3 text-sm"
                        >
                          <option value="all">All Status</option>
                          <option value="present">Present</option>
                          <option value="late">Late</option>
                          <option value="leave">Leave</option>
                          <option value="absent">Absent</option>
                          <option value="not marked">Not Marked</option>
                        </select>

                        <Input
                          placeholder="Search staff..."
                          value={attendanceSearch}
                          onChange={(e) => setAttendanceSearch(e.target.value)}
                          className="h-10 rounded-xl w-48"
                        />

                        <select
                          value={attendanceStaff}
                          onChange={(e) => setAttendanceStaff(e.target.value)}
                          className="h-10 rounded-xl border border-input bg-background px-3 text-sm"
                        >
                          <option value="all">All Staff</option>

                          {staff.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-center">
                      <thead>
                        <tr className="border-b border-border text-left">
                          <th className="px-5 py-3 font-medium text-muted-foreground text-center">
                            Staff
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground text-center">
                            Role
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground text-center">
                            Status
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground text-center">
                            Note
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground text-center">
                            Action
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredStaff.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-5 py-10 text-center text-sm text-muted-foreground"
                            >
                              No records found
                            </td>
                          </tr>
                        ) : (
                          filteredStaff.map((s) => (
                            <tr
                              key={s.id}
                              className="border-b border-border last:border-0"
                            >
                              <td className="px-5 py-4 text-center font-medium">
                                {s.name}
                              </td>

                              <td className="px-5 py-4 text-center text-muted-foreground capitalize">
                                {s.role.replace("_", " ")}
                              </td>

                              <td className="px-5 py-4">
                                {(() => {
                                  const record = attendance.find(
                                    (a) =>
                                      a.staff_id === s.id &&
                                      a.date === attendanceDate,
                                  );

                                  const status = record?.status || "not marked";

                                  const statusClass = {
                                    present: "bg-emerald-100 text-emerald-700",
                                    late: "bg-amber-100 text-amber-700",
                                    leave: "bg-stone-200 text-stone-700",
                                    absent: "bg-red-100 text-red-700",
                                    "not marked": "bg-stone-100 text-stone-500",
                                  };

                                  return (
                                    <span
                                      className={cn(
                                        "rounded-full px-2.5 py-1 text-xs font-semibold capitalize",
                                        statusClass[status] ||
                                          statusClass["not marked"],
                                      )}
                                    >
                                      {status}
                                    </span>
                                  );
                                })()}
                              </td>

                              <td className="px-5 py-4 text-center text-muted-foreground">
                                {(() => {
                                  const record = attendance.find(
                                    (a) =>
                                      a.staff_id === s.id &&
                                      a.date === attendanceDate,
                                  );

                                  return record?.note || "—";
                                })()}
                              </td>

                              <td className="px-5 py-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => markAttendance(s, "present")}
                                    className="rounded-lg"
                                  >
                                    Present
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      openAttendanceNote(s, "leave")
                                    }
                                    className="rounded-lg"
                                  >
                                    Leave
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      openAttendanceNote(s, "absent")
                                    }
                                    className="rounded-lg"
                                  >
                                    Absent
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      openAttendanceNote(s, "late")
                                    }
                                    className="rounded-lg"
                                  >
                                    Late
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Monthly Attendance */}
                <div className="mt-6 rounded-2xl bg-card border border-border overflow-hidden">
                  <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                      <h2 className="font-semibold">
                        Monthly Attendance History
                      </h2>

                      <p className="text-sm text-muted-foreground">
                        {historyYear} ·{" "}
                        {new Date(
                          Number(historyYear),
                          Number(historyMonth) - 1,
                        ).toLocaleString("en-US", {
                          month: "long",
                        })}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <select
                        value={historyYear}
                        onChange={(e) => setHistoryYear(e.target.value)}
                        className="h-10 rounded-xl border border-input bg-background px-3 text-sm"
                      >
                        {Array.from(
                          { length: 5 },
                          (_, i) => now.getFullYear() - i,
                        ).map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>

                      <select
                        value={historyMonth}
                        onChange={(e) => setHistoryMonth(e.target.value)}
                        className="h-10 rounded-xl border border-input bg-background px-3 text-sm"
                      >
                        <option value="01">January</option>
                        <option value="02">February</option>
                        <option value="03">March</option>
                        <option value="04">April</option>
                        <option value="05">May</option>
                        <option value="06">June</option>
                        <option value="07">July</option>
                        <option value="08">August</option>
                        <option value="09">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-center">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="px-5 py-3 font-medium text-muted-foreground">
                            Staff
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground">
                            Present
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground">
                            Late
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground">
                            Leave
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground">
                            Absent
                          </th>

                          <th className="px-5 py-3 font-medium text-muted-foreground">
                            Not Marked
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {monthlySummary.map((s) => (
                          <tr
                            key={s.id}
                            className="border-b border-border last:border-0"
                          >
                            <td className="px-5 py-4 font-medium">{s.name}</td>

                            <td className="px-5 py-4">
                              <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                {dayLabel(s.present)}
                              </span>
                            </td>

                            <td className="px-5 py-4">
                              <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                                {dayLabel(s.late)}
                              </span>
                            </td>

                            <td className="px-5 py-4">
                              <span className="inline-flex rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-700">
                                {dayLabel(s.leave)}
                              </span>
                            </td>

                            <td className="px-5 py-4">
                              <span className="inline-flex rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
                                {dayLabel(s.absent)}
                              </span>
                            </td>

                            <td className="px-5 py-4">
                              <span className="inline-flex rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-600">
                                {dayLabel(s.notMarked)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {staff.length === 0 ? (
              <EmptyState
                icon={UserCog}
                title="No staff members yet"
                subtitle="Add your first team member to manage staff, logins and attendance."
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {staff.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-2xl bg-card border border-border p-5 flex items-center gap-4"
                    data-testid={`staff-${s.id}`}
                  >
                    <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center font-display font-bold text-lg">
                      {s.name.charAt(0)}
                    </span>

                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{s.name}</p>

                      <p className="text-xs text-muted-foreground capitalize">
                        {s.role.replace("_", " ")} · {s.phone}
                      </p>

                      <div className="flex items-center justify-between mt-2">
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize",
                            ATT[s.attendance_today] || ATT.present,
                          )}
                        >
                          {s.attendance_today}
                        </span>

                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEdit(s)}
                            className="rounded-lg"
                            data-testid={`staff-edit-${s.id}`}
                          >
                            Edit
                          </Button>

                          <button
                            title="Reset password"
                            onClick={() => openResetPassword(s)}
                            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700"
                            data-testid={`staff-pw-${s.id}`}
                          >
                            <KeyRound size={14} />
                          </button>

                          <button
                            title="Remove staff"
                            onClick={() => openDeleteConfirm(s)}
                            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50"
                            data-testid={`staff-delete-${s.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add / Edit Staff Dialog */}
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value);

          if (!value) {
            resetForm();
          }
        }}
      >
        <DialogContent
          className="bg-white sm:max-w-md max-h-[90vh] overflow-y-auto"
          data-testid="staff-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingStaff ? "Edit Staff" : "Add Staff"}
            </DialogTitle>
          </DialogHeader>

          <Input
            placeholder="Full name"
            value={form.name}
            onChange={(e) =>
              setForm({
                ...form,
                name: e.target.value,
              })
            }
            className="h-11 rounded-xl"
            data-testid="staff-name"
          />

          <Input
            placeholder="Phone"
            value={form.phone}
            onChange={(e) =>
              setForm({
                ...form,
                phone: e.target.value.replace(/\D/g, "").slice(0, 10),
              })
            }
            maxLength={10}
            inputMode="numeric"
            className="h-11 rounded-xl"
            data-testid="staff-phone"
          />

          {/* Role selector + menu-style suggested roles */}
          <div className="space-y-3" data-testid="staff-role">
            <select
              value={form.role}
              onChange={(e) =>
                setForm((current) => ({
                  ...current,
                  role: e.target.value,
                }))
              }
              className="h-11 w-full rounded-xl border border-input bg-background px-3 text-sm"
              data-testid="staff-role-select"
            >
              <option value="">Select role...</option>
              {customRoles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
              {editingStaff?.role &&
                !customRoles.some(
                  (role) => role.value === editingStaff.role,
                ) && (
                  <option value={editingStaff.role}>
                    {roleLabel(editingStaff.role)}
                  </option>
                )}
            </select>

            <div className="rounded-xl border border-border bg-stone-50/70 p-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Suggested Roles
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Choose one or create your own
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setNewRoleName("");
                    setAddRoleOpen(true);
                  }}
                  className="h-8 rounded-full px-3 text-xs font-medium"
                  data-testid="staff-add-role"
                >
                  <Plus size={14} className="mr-1" />
                  Create New
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {SUGGESTED_ROLES.map((label) => {
                  const value = roleValue(label);
                  const selected = form.role === value;

                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => addSuggestedRole(label)}
                      className={cn(
                        "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                        selected
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-white hover:border-primary hover:bg-primary/5 hover:text-primary",
                      )}
                      data-testid={`suggested-role-${value}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {customRoles.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No restaurant roles created yet. Use a suggestion or Create New.
              </p>
            )}

            {!form.role && (
              <p className="text-xs text-amber-600">
                Please select or create a role before creating the staff member.
              </p>
            )}
          </div>
          <div className="border-t border-border pt-3 space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Login Credentials
            </p>

            <p className="text-xs text-muted-foreground">
              Staff will use this email and password to sign in to StayServe.
            </p>

            <Input
              type="email"
              placeholder="Email address"
              value={form.email}
              readOnly={!!editingStaff}
              onChange={(e) =>
                setForm({
                  ...form,
                  email: e.target.value,
                })
              }
              className="h-11 rounded-xl"
              data-testid="staff-email"
            />

            {!editingStaff && (
              <Input
                type="password"
                placeholder="Password (min 6 characters)"
                value={form.password}
                onChange={(e) =>
                  setForm({
                    ...form,
                    password: e.target.value,
                  })
                }
                className="h-11 rounded-xl"
                data-testid="staff-password"
              />
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={saveStaff}
              className="w-full h-11 rounded-xl font-semibold"
              disabled={savingStaff}
              data-testid="staff-save"
            >
              {savingStaff
                ? editingStaff
                  ? "Saving..."
                  : "Creating..."
                : editingStaff
                  ? "Save Changes"
                  : "Add Staff & Create Login"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Role Dialog */}
      <Dialog
        open={addRoleOpen}
        onOpenChange={(value) => {
          if (!value) {
            closeAddRole();
          } else {
            setAddRoleOpen(true);
          }
        }}
      >
        <DialogContent className="bg-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Add Role</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">
            Create a role for this restaurant. After it is added, configure its
            module access from Roles & Permissions.
          </p>

          <Input
            autoFocus
            placeholder="e.g. Floor Manager"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !savingRole) {
                e.preventDefault();
                saveCustomRole();
              }
            }}
            className="h-11 rounded-xl"
            data-testid="staff-new-role"
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeAddRole}
              disabled={savingRole}
              className="rounded-xl"
            >
              Cancel
            </Button>

            <Button
              type="button"
              onClick={saveCustomRole}
              disabled={savingRole}
              className="rounded-xl font-semibold"
              data-testid="staff-save-role"
            >
              {savingRole ? "Adding..." : "Add Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="bg-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Reset Password</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">
            Set a new password for <strong>{pwStaff?.name}</strong>
          </p>

          <Input
            type="password"
            placeholder="New password (min 6 characters)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="h-11 rounded-xl"
          />

          <DialogFooter>
            <Button
              onClick={savePassword}
              className="w-full h-11 rounded-xl font-semibold"
            >
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendance Note Dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display capitalize">
              Mark {noteStatus}
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">{noteStaff?.name}</p>

          <Input
            placeholder="Add note (optional)"
            value={attendanceNote}
            onChange={(e) => setAttendanceNote(e.target.value)}
            className="h-11 rounded-xl"
          />

          <DialogFooter>
            <Button
              onClick={async () => {
                await markAttendance(noteStaff, noteStatus, attendanceNote);
                setNoteOpen(false);
              }}
              className="w-full h-11 rounded-xl font-semibold"
            >
              Save Attendance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Common Delete Staff Confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        title="Remove staff?"
        message={
          confirmStaff
            ? `Remove ${confirmStaff.name} from the team? Their login will be deactivated.`
            : "Remove this staff member from the team?"
        }
        confirmText={deletingStaff ? "Removing..." : "Remove"}
        cancelText="Cancel"
        onConfirm={deleteStaff}
        onCancel={closeDeleteConfirm}
        loading={deletingStaff}
        danger
      />
    </>
  );
}
